import React, { FunctionComponent, useMemo } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { parseISO } from 'date-fns'

import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { BadgeVariant } from '@lyrahealth-inc/ui-core-crossplatform/src/badge/constants'
import { AddNewClientModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/addNewClientModal/AddNewClientModal'
import { AddNewClientsAccordion } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/addNewClientsAccordion/AddNewClientsAccordion'
import { ConfirmationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/confirmationModal/ConfirmationModal'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { useAddNewClientModals } from './hooks/useAddNewClientModals'
import { useClientRegistrationActions } from './hooks/useClientRegistrationActions'
import { useNewClientHoverActions } from './hooks/useNewClientHoverActions'
import { track } from '../../../../../../mixpanel/mixpanelTracking'
import { ClientRegistration, useGetClientRegistrationsQuery } from '../../../../addNewClients/data/addNewClientApi'
import {
  selectCompletedRegistrations,
  selectOutstandingRegistrations,
} from '../../../../addNewClients/data/addNewClientSelectors'
import { getAuthUserId } from '../../../../data/auth/authSelectors'

const getBadgeLabel = (intl: IntlShape, status: ClientRegistration['registration_status']): string => {
  switch (status) {
    case 'pending':
      return intl.formatMessage({
        defaultMessage: 'Pending',
        description: 'Badge label for pending client registration',
      })
    case 'expired':
      return intl.formatMessage({
        defaultMessage: 'Expired',
        description: 'Badge label for expired client registration',
      })
    case 'completed':
      return intl.formatMessage({
        defaultMessage: 'Ready to Schedule',
        description: 'Badge label for completed client registration',
      })
    default:
      return intl.formatMessage({
        defaultMessage: 'Pending',
        description: 'Badge label default for client registration',
      })
  }
}

const getBadgeVariant = (status: ClientRegistration['registration_status']): BadgeVariant => {
  switch (status) {
    case 'completed':
      return BadgeVariant.INFO_PRIMARY
    default:
      return BadgeVariant.NEUTRAL
  }
}

const formatDate = (intl: IntlShape, dateString: string | null): string => {
  if (!dateString) return ''
  try {
    return intl.formatDate(parseISO(dateString), { month: 'numeric', day: 'numeric', year: 'numeric' })
  } catch {
    return dateString
  }
}

const buildDetails = (intl: IntlShape, reg: ClientRegistration): string => {
  const program = getProgramDisplayLabel(intl, reg.program_name, 'daClientList')
  const datePart = reg.last_registration_link_sent
    ? intl.formatMessage(
        {
          defaultMessage: 'Invite sent: {date}',
          description: 'Detail showing when client registration invite was sent',
        },
        { date: formatDate(intl, reg.last_registration_link_sent) },
      )
    : ''
  return [program, reg.email, datePart].filter(Boolean).join(' \u2022 ')
}

export const AddNewClientsList: FunctionComponent = () => {
  const intl = useIntl()
  const userId = useSelector(getAuthUserId)

  useGetClientRegistrationsQuery({ providerId: userId })

  const completedClientsResult = useSelector(selectCompletedRegistrations(userId))
  const outstandingClientsResult = useSelector(selectOutstandingRegistrations(userId))
  const completedClients = useMemo(() => completedClientsResult ?? [], [completedClientsResult])
  const outstandingClients = useMemo(() => outstandingClientsResult ?? [], [outstandingClientsResult])
  const invitedClients = useMemo(
    () => [...completedClients, ...outstandingClients],
    [completedClients, outstandingClients],
  )

  const {
    allCustomers,
    showAddNewClientModal,
    showSuccessModal,
    addedClientFirstName,
    selectedCompany,
    submissionError,
    isAddingNewClient,
    careTypeOptions,
    isFetchingPrograms,
    addNewClient,
    openAddClientModal,
    closeAddClientModal,
    closeSuccessModal,
    setSelectedCompany,
    setAddedClientFirstName,
    setShowSuccessModal,
    setSubmissionError,
    setToastContent: dispatchToast,
  } = useAddNewClientModals(userId)

  const {
    showDeleteConfirmModal,
    deleteTarget,
    handleResendPress,
    handleDeletePress,
    handleConfirmDelete,
    closeDeleteConfirmModal,
  } = useClientRegistrationActions()

  const getHoverActions = useNewClientHoverActions({
    onDeletePress: handleDeletePress,
    onResendPress: handleResendPress,
  })

  const clientListItems = useMemo(
    () =>
      invitedClients.map((reg) => ({
        id: reg.id,
        clientName: `${reg.first_name} ${reg.last_name}`,
        details: buildDetails(intl, reg),
        badgeLabel: getBadgeLabel(intl, reg.registration_status),
        badgeVariant: getBadgeVariant(reg.registration_status),
        showNotificationDot: reg.registration_status === 'completed',
        hoverActions: getHoverActions(reg),
      })),
    [invitedClients, intl, getHoverActions],
  )

  const addClientButton = (
    <PrimaryButton
      text={<FormattedMessage defaultMessage='Add client' description='Add client button' />}
      onPress={openAddClientModal}
      testID={tID('AddNewClientsList-addClient')}
    />
  )

  return (
    <>
      <AddNewClientsAccordion
        clientInvitations={clientListItems}
        registeredCount={completedClients.length}
        addNewClientButton={addClientButton}
      />
      <ConfirmationModal
        visible={showDeleteConfirmModal}
        modalTitle={
          <FormattedMessage
            defaultMessage='Remove {firstName} {lastName}?'
            description='Title of confirmation modal when removing an invited client'
            values={{
              firstName: deleteTarget?.first_name ?? '',
              lastName: deleteTarget?.last_name ?? '',
            }}
          />
        }
        modalContents={
          deleteTarget?.registration_status !== 'completed' ? (
            <FormattedMessage
              defaultMessage='This will cancel the invitation. The client will be removed from your invite list.'
              description='Body text for removing a pending or expired invited client'
            />
          ) : (
            <FormattedMessage
              defaultMessage='They will be removed from this list. You can still find their profile in your inactive clients list if you need them later.'
              description='Body text for removing a registered client who has never booked'
            />
          )
        }
        confirmationButtonText={
          <FormattedMessage defaultMessage='Remove' description='Button to confirm removing an invited client' />
        }
        cancelButtonText={
          <FormattedMessage defaultMessage='Cancel' description='Button to cancel removing an invited client' />
        }
        onConfirmationButtonPress={handleConfirmDelete}
        onRequestClose={closeDeleteConfirmModal}
        testID={tID('AddNewClientsList-deleteConfirmModal')}
      />
      <AddNewClientModal
        customers={allCustomers}
        providerId={userId}
        visible={showAddNewClientModal}
        onAddNewClient={addNewClient}
        isSubmitting={isAddingNewClient}
        onRequestClose={closeAddClientModal}
        track={track}
        careTypeOptions={careTypeOptions}
        isFetchingCareTypes={isFetchingPrograms}
        onCompanyChange={setSelectedCompany}
        selectedCompany={selectedCompany}
        setAddedClientFirstName={setAddedClientFirstName}
        setShowSuccessModal={setShowSuccessModal}
        setToastContent={dispatchToast}
        submissionError={submissionError}
        setSubmissionError={setSubmissionError}
      />
      <ConfirmationModal
        visible={showSuccessModal}
        modalTitle={
          <FormattedMessage
            defaultMessage='{clientFirstName} has been added to your invited list.'
            description='Title confirming a new client was successfully added'
            values={{ clientFirstName: addedClientFirstName ?? '' }}
          />
        }
        modalContents={
          <FormattedMessage
            defaultMessage='We sent them a registration link that expires in 14 days. You can book a session as soon as they complete registration.'
            description='Body text explaining what happens after a new client is added'
          />
        }
        confirmationButtonText={
          <FormattedMessage
            defaultMessage='Done'
            description='Button to dismiss the success modal after adding a new client'
          />
        }
        showCancel={false}
        onConfirmationButtonPress={closeSuccessModal}
        onRequestClose={closeSuccessModal}
        testID={tID('AddNewClientSuccessModal')}
      />
    </>
  )
}
